import { useState, useCallback, type ReactNode, type SyntheticEvent } from 'react';
import { Children } from 'react';
// -*- mode: jsx;-*-
import { KeyboardClickable } from './KeyboardClickable.js';
import { Svg } from './Svg.js';
import { SHARE_TOOLS, trackShareToolClick } from './shareToolUtils.js';
import { appendToTrackingLinkRF } from './shareBarUtils';
import { setUpOptimizelyListener, useOptimizelyVariant } from './optimizely/OptimizelyTesting';
import { truncate } from 'lodash';

const MAX_BODY_LENGTH = 1000;

const safeNextdoorURL = (body:string, link:string, outro?:string) => {
    // default to the event driven share body
    body = shareBody || body;
    // Add spaces so Nextdoor doesn't assume they are framing a url :eyeroll:
    body = body.replace(/(\S+)\.(\S+)/g, '$1. $2');
    // Add a hashcode if set
    const shareTag = hashtag ? `&hashtag=${hashtag}` : '';
    // Append any A/B test rfs
    link = appendToTrackingLinkRF(link, nextdoorRF);
    // Pad the outro with newlines if it exists
    outro = outro ? `\n\n${outro}` : '';
    // Trim it if needed
    body = truncate(body, { length: MAX_BODY_LENGTH - link.length - outro.length, separator: ' ' });
    return `https://nextdoor.com/sharekit/?source=DonorsChoose${shareTag}&body=${encodeURIComponent(body + outro)}` + link;
};

// All of the link logic lives in this class.
const NEXTDOOR_EVENT = 'nd-36268';
let [nextdoorRF, shareBody, hashtag] = [null, null, ''];

interface NextDoorEvent extends CustomEvent {
    rf?: string
    body?: string
    hashtag?: string
}

setUpOptimizelyListener<NextDoorEvent>(NEXTDOOR_EVENT, (event) => {
    console.log('Nextdoor A/B test update', event.detail);
    nextdoorRF = event.detail.rf;
    shareBody = event.detail.body;
    hashtag = event.detail.hashtag;
});

interface NextdoorLinkProps {
    urlToShare: string,
    pageName: string,
    getTrackingLink: typeof window.getTrackingLink,
    useSpan?: boolean,
    className?: string,
    onNextdoorClick: (event:SyntheticEvent, url:string) => void,
    children?: ReactNode,
    schoolName: string,
    projectSummary?: string,
    nextdoorShareBody?: string,
    projectId?: number,
    essentialsListShare?: boolean,
    isTeacher?: boolean,
}

export const NextdoorLink = ({
    useSpan, pageName, getTrackingLink, urlToShare, className, onNextdoorClick, children,
    schoolName, projectSummary, nextdoorShareBody, projectId, essentialsListShare, isTeacher = true,
}:NextdoorLinkProps) => {
    const getNextdoorUrl = useCallback(() => {
        const paramLinks = getTrackingLink(urlToShare, 'nextdoor', pageName);
        if (shareBody || nextdoorShareBody) {
            const body = shareBody || nextdoorShareBody;
            return safeNextdoorURL(body!, paramLinks);
        }
        const an = /^[aeiou]/i.test(schoolName) ? 'an' : 'a';
        const messageParts:string[] = [];
        const intro = isTeacher
            ? `I’m ${an} ${schoolName} teacher, and my students could use a hand this year.`
            : `Check out this ${schoolName} teacher whose students could use a hand this year.`;
        messageParts.push(intro);
        if (projectSummary) {
            messageParts.push(projectSummary);
        }
        const outro = `Support ${isTeacher ? 'my' : 'those'} students here: `;
        const body = messageParts.join('\n\n');
        return safeNextdoorURL(body, paramLinks, outro);
    }, [getTrackingLink, isTeacher, nextdoorShareBody, pageName, projectSummary, schoolName, urlToShare]);

    const [shareUrl, setShareUrl] = useState(getNextdoorUrl());

    useOptimizelyVariant<NextDoorEvent>(
        NEXTDOOR_EVENT,
        () => { setTimeout(() => setShareUrl(getNextdoorUrl()), 20); },
        null,
        [getNextdoorUrl],
    );

    if (!nextdoorShareBody && !schoolName) {
        console.error('Missing required props for NextdoorLink: either nextdoorShareBody or schoolName must be provided');
        return;
    }

    const shareIcon = () => {
        return Children.count(children) > 0 ? null : <Svg icon="ss-nextdoor" iconFamily="social-circle"/>;
    };

    const onClick = (event:SyntheticEvent) => {
        window.dcAnalytics.trackShareToolClick(pageName, urlToShare, 'Nextdoor', essentialsListShare);
        trackShareToolClick(projectId, SHARE_TOOLS.NEXTDOOR);
        onNextdoorClick(event, getNextdoorUrl());
    };

    return useSpan
        ? <KeyboardClickable>
            <span tabIndex={0}
                onClick={onClick}
                // Key press handled by KeyboardClickable
                onKeyDown={() => {}}
                role='button'
                aria-label="Share on Nextdoor"
                className={className + ' heap-nextdoor-share'}>
                {shareIcon()}
                {children}
            </span>
        </KeyboardClickable>
        : <a href={shareUrl}
            onClick={onClick}
            target="_blank"
            aria-label="Share on Nextdoor"
            className={className + ' heap-nextdoor-share'} rel="noreferrer">
            {shareIcon()}
            {children}
        </a>;
};
